(() => {
  'use strict';

  const STORAGE_KEY = 'u20:vending-tunnel-transition:v1';
  const STATE_TTL = 15000;
  const TUNNEL_DURATION = 840;
  const REDUCED_DURATION = 180;
  const ARRIVAL_DURATION = 880;
  const MOBILE_QUERY = window.matchMedia('(max-width: 749px)');
  const REDUCED_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');
  const root = document.documentElement;
  const overlay = document.querySelector('[data-u20-vending-tunnel-overlay]');
  const canvas = document.querySelector('[data-u20-vending-tunnel-canvas]');

  if (!overlay || !canvas) return;

  let context = null;
  let frameId = 0;
  let arrivalTimer = 0;
  let startTime = 0;
  let lastPaint = 0;
  let targetUrl = '';
  let navigating = false;

  const cleanPath = (value) => {
    try {
      const url = new URL(value, window.location.href);
      return `${url.pathname.replace(/\/$/, '')}${url.search}`;
    } catch (_) {
      return '';
    }
  };

  const readState = () => {
    try {
      const raw = window.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const state = JSON.parse(raw);
      if (!state || state.expires <= Date.now() || state.targetPath !== cleanPath(window.location.href)) return null;
      return state;
    } catch (_) {
      return null;
    }
  };

  const writeState = (url) => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        sourcePath: cleanPath(window.location.href),
        targetPath: cleanPath(url),
        expires: Date.now() + STATE_TTL
      }));
      return true;
    } catch (_) {
      return false;
    }
  };

  const clearState = () => {
    try { window.sessionStorage.removeItem(STORAGE_KEY); } catch (_) {}
  };

  // BFCache and some in-app browsers can restore the selected product's href
  // without restoring attributes that were added after the original parse.
  // Resolve all twelve vending URLs from product data as a durable identity.
  const isVendingProductLink = (link) => {
    if (!link) return false;
    if (link.hasAttribute('data-u20-vending-tunnel')) return true;
    if (!link.matches?.('[data-u20-vending-product][href]')) return false;

    const vending = link.closest('[data-u20-vending]');
    const data = vending?.querySelector?.('[data-u20-vending-products]');
    if (!data) return false;
    try {
      const products = JSON.parse(data.textContent || '[]');
      if (!Array.isArray(products)) return false;
      const currentPath = cleanPath(link.href);
      return products.slice(0, 12).some((product) => product?.url && cleanPath(product.url) === currentPath);
    } catch (_) {
      return false;
    }
  };

  const cancelFrame = () => {
    if (frameId) window.cancelAnimationFrame(frameId);
    frameId = 0;
  };

  const release = () => {
    cancelFrame();
    window.clearTimeout(arrivalTimer);
    arrivalTimer = 0;
    targetUrl = '';
    navigating = false;
    root.classList.remove('u20-vending-tunnel-leaving', 'u20-vending-tunnel-arrival', 'u20-vending-tunnel-canvas-ready', 'u20-vending-tunnel-canvas-fallback');
    root.style.removeProperty('--u20-vending-tunnel-aperture-scale');
    overlay.setAttribute('aria-hidden', 'true');
    canvas.width = 1;
    canvas.height = 1;
    context = null;
  };

  const resizeCanvas = () => {
    const width = Math.max(1, window.innerWidth || document.documentElement.clientWidth || 1);
    const height = Math.max(1, window.innerHeight || document.documentElement.clientHeight || 1);
    const ratio = Math.min(window.devicePixelRatio || 1, 1.25);
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    context = canvas.getContext('2d', {alpha: true, desynchronized: true});
    if (context) context.setTransform(ratio, 0, 0, ratio, 0, 0);
    return {width, height};
  };

  const drawTunnel = (now) => {
    if (!context || !startTime) return;
    const elapsed = now - startTime;
    const progress = Math.min(1, Math.max(0, elapsed / TUNNEL_DURATION));

    // Cap this short decorative effect to 45fps. No frame loop exists before
    // the click or after navigation begins.
    if (progress < 1 && now - lastPaint < 1000 / 45) {
      frameId = window.requestAnimationFrame(drawTunnel);
      return;
    }
    lastPaint = now;

    const width = canvas.clientWidth || window.innerWidth;
    const height = canvas.clientHeight || window.innerHeight;
    const shortest = Math.min(width, height);
    const centerX = width * .5;
    const centerY = height * .5;
    const cover = .92;

    context.clearRect(0, 0, width, height);
    context.fillStyle = `rgba(1, 9, 14, ${cover * .92})`;
    context.fillRect(0, 0, width, height);

    const glow = context.createRadialGradient(centerX, centerY, 0, centerX, centerY, shortest * .72);
    glow.addColorStop(0, `rgba(48, 190, 214, ${progress * .18})`);
    glow.addColorStop(.34, `rgba(13, 100, 123, ${progress * .28})`);
    glow.addColorStop(1, 'rgba(0, 4, 8, 0)');
    context.fillStyle = glow;
    context.fillRect(0, 0, width, height);

    context.save();
    context.translate(centerX, centerY);
    context.globalCompositeOperation = 'lighter';

    const ringCount = 32;
    for (let index = 0; index < ringCount; index += 1) {
      const phase = (index / ringCount + progress * 1.85) % 1;
      const radius = shortest * .021 * Math.pow(39, phase);
      const alpha = Math.sin(phase * Math.PI) * Math.min(1, .22 + progress * 4) * .62;
      const wobble = Math.sin(index * 1.73 + progress * 8.5);
      const xRadius = radius * (1.12 + wobble * .035);
      const yRadius = radius * (.76 - wobble * .024);
      const rotation = progress * .34 + index * .018;

      context.beginPath();
      context.ellipse(wobble * radius * .018, -wobble * radius * .012, xRadius, yRadius, rotation, 0, Math.PI * 2);
      context.lineWidth = .55 + phase * 3.8;
      context.strokeStyle = `rgba(65, 220, 240, ${alpha * .9})`;
      context.stroke();

      if (index % 2 === 0) {
        context.beginPath();
        context.ellipse(0, 0, xRadius * .985, yRadius * .985, rotation, -.55, 1.9);
        context.lineWidth = .35 + phase * 1.65;
        context.strokeStyle = `rgba(174, 247, 255, ${alpha * .66})`;
        context.stroke();
      }
    }

    for (let index = 0; index < 18; index += 1) {
      const angle = index / 18 * Math.PI * 2 + progress * .22;
      const inner = shortest * (.045 + progress * .018);
      const outer = Math.max(width, height) * .82;
      context.beginPath();
      context.moveTo(Math.cos(angle) * inner, Math.sin(angle) * inner * .72);
      context.lineTo(Math.cos(angle + .05) * outer, Math.sin(angle + .05) * outer * .72);
      context.lineWidth = 1 + progress * 1.4;
      context.strokeStyle = `rgba(29, 157, 185, ${.06 + progress * .16})`;
      context.stroke();
    }
    context.restore();

    // The destination page is physically below this canvas. Cutting a real
    // transparent aperture means its first visible pixel is product-page UI,
    // never a painted black core or a simulated screenshot.
    const eased = 1 - Math.pow(1 - progress, 3);
    const apertureScale = 2.2 + eased * 27.8;
    const apertureRadius = shortest * .055 * apertureScale;
    context.save();
    context.globalCompositeOperation = 'destination-out';
    context.beginPath();
    context.arc(centerX, centerY, apertureRadius, 0, Math.PI * 2);
    context.fill();
    context.restore();
    root.style.setProperty('--u20-vending-tunnel-aperture-scale', apertureScale.toFixed(3));
    root.classList.add('u20-vending-tunnel-canvas-ready');

    if (progress < 1) frameId = window.requestAnimationFrame(drawTunnel);
  };

  const navigateNow = () => {
    if (!targetUrl || navigating) return;
    navigating = true;
    cancelFrame();
    window.location.assign(targetUrl);
  };

  const prefetch = (url) => {
    try {
      const hint = document.createElement('link');
      hint.rel = 'prefetch';
      hint.href = url;
      hint.fetchPriority = 'high';
      document.head.appendChild(hint);
    } catch (_) {}
  };

  const beginDeparture = (url) => {
    if (!url || targetUrl || !MOBILE_QUERY.matches) return false;
    let target;
    try {
      target = new URL(url, window.location.href);
      if (target.origin !== window.location.origin || !target.pathname.includes('/products/')) return false;
    } catch (_) {
      return false;
    }

    targetUrl = target.href;
    writeState(targetUrl);
    prefetch(targetUrl);
    navigateNow();
    return true;
  };

  // Public entry point used by the mobile capsule machine. Keeping the
  // navigation state in this single controller also makes browser Back replay
  // the same cyan arrival transition as the original vending-machine links.
  window.U20VendingTunnel = Object.assign(window.U20VendingTunnel || {}, {
    open: beginDeparture,
    release
  });

  const handleClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target?.closest?.('a[data-u20-vending-product][href], a[data-u20-vending-tunnel][href]');
    if (!link || link.target && link.target !== '_self' || link.hasAttribute('download')) return;
    if (!isVendingProductLink(link)) return;
    if (!beginDeparture(link.href)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  const playArrival = () => {
    const pending = readState();
    if (!pending && !root.classList.contains('u20-vending-tunnel-arrival')) return;
    clearState();
    overlay.setAttribute('aria-hidden', 'false');
    root.classList.add('u20-vending-tunnel-arrival');
    if (!REDUCED_QUERY.matches) {
      const size = resizeCanvas();
      if (context && size.width > 0 && size.height > 0) {
        startTime = window.performance.now();
        lastPaint = startTime - 100;
        frameId = window.requestAnimationFrame(drawTunnel);
      } else {
        root.classList.add('u20-vending-tunnel-canvas-fallback');
      }
    }
    arrivalTimer = window.setTimeout(release, REDUCED_QUERY.matches ? REDUCED_DURATION + 40 : ARRIVAL_DURATION);
  };

  document.addEventListener('click', handleClick, true);
  window.addEventListener('pagehide', release, {capture: true});
  window.addEventListener('pageshow', (event) => {
    if (event.persisted || targetUrl || navigating || root.classList.contains('u20-vending-tunnel-leaving')) release();
  }, {capture: true});
  document.addEventListener('visibilitychange', () => {
    if (document.hidden && targetUrl && !navigating) navigateNow();
  });
  document.addEventListener('shopify:section:unload', cancelFrame);

  playArrival();
})();
